// Svelte 5 no longer needs svelte/store imports for basic reactivity
import { environment } from "@app/store/envStore.svelte.js";
import { authState } from "@app/store/authStore.svelte.js";
import db from "@app/configs/firestore.js";
import { doc, updateDoc } from "firebase/firestore";

/**
 * Visual watermarking for the media player.
 *
 * Settings are owned by the team (admins configure them for everyone on the
 * team) and stored on the team document under the `watermark` field. They are
 * delivered to each member with the rest of the team document at auth time, so
 * no extra read is required on startup.
 *
 * Third party hosts embedding the editor in an IFrame can push their own
 * settings at runtime with the `setWatermark` remote method. Host settings are
 * fully resolved by the host (the date/time, email, and IP are supplied as
 * strings) and take precedence over any team configuration.
 */

export const WATERMARK_ALIGNMENTS = ["top", "center", "bottom"];

/* Sources, in order of precedence. A host that calls setWatermark owns the
   watermark for the rest of the session. */
export const WATERMARK_SOURCE_TEAM = "team";
export const WATERMARK_SOURCE_REMOTE = "remote";

function clampOpacity(value, fallback = 100) {
    const num = Number(value);
    if (!Number.isFinite(num)) return fallback;
    return Math.min(100, Math.max(0, Math.round(num)));
}

function normalizeAlignment(value, fallback = "center") {
    return WATERMARK_ALIGNMENTS.includes(value) ? value : fallback;
}

function normalizeText(value) {
    return typeof value === "string" ? value.trim() : "";
}

/** Settings shape stored on the team document. */
export function defaultWatermarkSettings() {
    return {
        enabled: false,
        showDateTime: true,
        showEmail: true,
        showIpAddress: true,
        showCustomText: false,
        customText: "",
        alignment: "center",
        textOpacity: 100,
        shadowOpacity: 100,
    };
}

/** Coerce a partial/untrusted settings object into the team settings shape. */
export function normalizeWatermarkSettings(settings) {
    const defaults = defaultWatermarkSettings();
    if (!settings || typeof settings !== "object") return defaults;

    return {
        enabled: settings.enabled === true,
        showDateTime: settings.showDateTime !== false,
        showEmail: settings.showEmail !== false,
        showIpAddress: settings.showIpAddress !== false,
        showCustomText: settings.showCustomText === true,
        customText: normalizeText(settings.customText),
        alignment: normalizeAlignment(settings.alignment, defaults.alignment),
        textOpacity: clampOpacity(settings.textOpacity, defaults.textOpacity),
        shadowOpacity: clampOpacity(settings.shadowOpacity, defaults.shadowOpacity),
    };
}

/** Format a date as "YYYY-MM-DD HH:mm:ss" in the viewer's local time. */
export function formatWatermarkDateTime(date = new Date()) {
    const pad = (value) => String(value).padStart(2, "0");
    return (
        `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
        `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    );
}

export const watermarkState = $state({
    /* ── Team configurable settings ────────────────────────────────── */
    ...defaultWatermarkSettings(),

    /* ── Resolved runtime values ───────────────────────────────────── */
    /** Viewer email, or null when unavailable/disabled */
    email: null,
    /** Viewer IP address, or null when unavailable/disabled */
    ipAddress: null,
    /** Fixed timestamp supplied by a host. Null means use a live clock. */
    dateTime: null,
    /** Which configuration is currently applied */
    source: WATERMARK_SOURCE_TEAM,
});

/** True when there is something to draw over the player. */
export function isWatermarkVisible() {
    if (!watermarkState.enabled) return false;
    return (
        watermarkState.showDateTime ||
        (watermarkState.showEmail && !!watermarkState.email) ||
        (watermarkState.showIpAddress && !!watermarkState.ipAddress) ||
        (watermarkState.showCustomText && !!watermarkState.customText)
    );
}

/**
 * Build the lines rendered by the overlay.
 * The identity fields share a line; custom text gets its own.
 * @param {string|null} liveDateTime Current clock value (team mode only)
 */
export function getWatermarkLines(liveDateTime = null) {
    const lines = [];
    const identity = [];

    if (watermarkState.showDateTime) {
        const stamp = watermarkState.dateTime || liveDateTime;
        if (stamp) identity.push(stamp);
    }

    if (watermarkState.showEmail && watermarkState.email) {
        identity.push(watermarkState.email);
    }

    if (watermarkState.showIpAddress && watermarkState.ipAddress) {
        identity.push(watermarkState.ipAddress);
    }

    if (identity.length > 0) lines.push(identity.join(" "));

    if (watermarkState.showCustomText && watermarkState.customText) {
        lines.push(watermarkState.customText);
    }

    return lines;
}

function applySettings(settings) {
    const normalized = normalizeWatermarkSettings(settings);
    Object.keys(normalized).forEach((key) => {
        watermarkState[key] = normalized[key];
    });
}

/**
 * Apply the watermark configured on the current team. Safe to call when the
 * user has no team (clears the watermark) and is skipped once a host has taken
 * over with setWatermark.
 */
export function initTeamWatermark() {
    if (watermarkState.source === WATERMARK_SOURCE_REMOTE) return;

    applySettings(authState.team?.watermark);

    watermarkState.source = WATERMARK_SOURCE_TEAM;
    watermarkState.dateTime = null;
    watermarkState.email = authState.user?.email || null;
    watermarkState.ipAddress = authState.ipAddress || null;
}

/**
 * Persist the team watermark settings. Team admins only.
 * @param {object} settings Partial/complete team settings object
 */
export async function saveTeamWatermark(settings) {
    if (!environment.online) {
        throw new Error("Watermark settings require an online session.");
    }

    const teamId = authState.team?.id;
    if (!teamId) {
        throw new Error("Watermark settings are only available to teams.");
    }

    if (authState.user?.role !== "admin") {
        throw new Error("Only team admins can change watermark settings.");
    }

    const watermark = {
        ...normalizeWatermarkSettings(settings),
        updatedAt: new Date().toISOString(),
        updatedBy: authState.user?.email || null,
    };

    await updateDoc(doc(db, "teams", teamId), { watermark });

    /* Keep the cached team document in sync so a reopen of the modal (or a
       later initTeamWatermark) sees the saved values without a refetch. */
    authState.team = { ...authState.team, watermark };

    if (watermarkState.source !== WATERMARK_SOURCE_REMOTE) {
        initTeamWatermark();
    }

    return watermark;
}

/**
 * Apply watermark settings pushed by an IFrame host.
 * The host resolves the display values itself, so `dateTime`, `email`, and
 * `ipAddress` arrive as strings (or null when that field is off).
 * @param {object} settings
 */
export function applyRemoteWatermark(settings) {
    if (!settings || typeof settings !== "object") {
        throw new Error("Watermark settings object is required.");
    }

    const dateTime = normalizeText(settings.dateTime);
    const email = normalizeText(settings.email);
    const ipAddress = normalizeText(settings.ipAddress);
    const customText = normalizeText(settings.customText);

    applySettings({
        enabled: settings.enabled !== false,
        showDateTime: settings.showDateTime === true && !!dateTime,
        showEmail: !!email,
        showIpAddress: !!ipAddress,
        showCustomText: !!customText,
        customText,
        alignment: settings.alignment,
        textOpacity: settings.textOpacity,
        shadowOpacity: settings.shadowOpacity,
    });

    watermarkState.source = WATERMARK_SOURCE_REMOTE;
    watermarkState.dateTime = dateTime || null;
    watermarkState.email = email || null;
    watermarkState.ipAddress = ipAddress || null;

    return {
        enabled: watermarkState.enabled,
        showDateTime: watermarkState.showDateTime,
        dateTime: watermarkState.dateTime,
        email: watermarkState.email,
        ipAddress: watermarkState.ipAddress,
        customText: watermarkState.customText || null,
        alignment: watermarkState.alignment,
        textOpacity: watermarkState.textOpacity,
        shadowOpacity: watermarkState.shadowOpacity,
    };
}

/** Remove the watermark and release host ownership. */
export function resetWatermarkState() {
    applySettings(null);
    watermarkState.source = WATERMARK_SOURCE_TEAM;
    watermarkState.dateTime = null;
    watermarkState.email = null;
    watermarkState.ipAddress = null;
}
